// Updated: 2026-08-14 21:00 CDT
// Purpose: Restore persisted screenplay state and choose the correct editor-aware workflow step.

import type { Json } from "@/integrations/supabase/types";
import type { Character } from "@/types/screenplay";
import { assessVoiceAssignments } from "@/utils/voiceAssignments";

export type WorkflowStep = 1 | 2 | 3 | 4;

const isCharacter = (candidate: unknown): candidate is Character => {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return false;
  }

  const record = candidate as Record<string, unknown>;

  return (
    typeof record.name === "string" &&
    typeof record.lines === "number" &&
    typeof record.description === "string"
  );
};

export const restoreCharacters = (value: Json): Character[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return (value as unknown[]).filter(isCharacter);
};

export const restoreAssignments = (value: Json): Record<string, string> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
};

export const restoreParseWarnings = (value: Json): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (warning): warning is string =>
      typeof warning === "string" && warning.trim().length > 0,
  );
};

export const determineResumeStep = (
  characters: Character[],
  assignments: Record<string, string>,
  latestJobStatus: string | null,
): WorkflowStep => {
  if (characters.length === 0) {
    return 1;
  }

  const assignmentsComplete = assessVoiceAssignments(characters, assignments).isComplete;

  if (assignmentsComplete && latestJobStatus) return 4;
  if (assignmentsComplete) return 3;
  return 2;
};
